import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, UseFormReturn } from "react-hook-form";
import RecurringBookingFormInterface, {
  RecurringBookingFormMain,
} from "../../../services/recurringBookingInterfaces";
import dayjs from "dayjs";

const RecurringBookingMainForm = (props: {
  methods: UseFormReturn<RecurringBookingFormInterface>;
}) => {
  return (
    <Box
      borderRadius={2}
      border="1px solid #ccc"
      sx={{ backgroundColor: "#FFFFFF" }}
    >
      {/* Data Inizio Ricerca + Numero Prenotazione */}
      <Grid container spacing={2} padding={2}>
        <Grid item xs={12} sm={6}>
          <Controller
            name="recurringBooking.dataInizioRicerca"
            control={props.methods.control}
            rules={{
              required: "Campo obbligatorio",
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Data Inizio Ricerca"
                placeholder="Inserire Data"
                required
                inputProps={{
                  min: dayjs().format("YYYY-MM-DD"),
                  max: "9999-12-31",
                }}
                type="date"
                value={field.value ?? ""}
                fullWidth
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                size="small"
                InputLabelProps={{ shrink: true }}
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <Controller
            name="recurringBooking.numeroPrenotazione"
            control={props.methods.control}
            rules={{
              validate: (value) => {
                if (!value) return true;
                return (
                  /^\d{12,}$/.test(value) ||
                  "Il numero deve contenere almeno 12 cifre"
                );
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                label="Numero Prenotazione"
                placeholder="Inserire Numero Prenotazione"
                variant="outlined"
                value={field.value ?? ""}
                fullWidth
                size="small"
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
              />
            )}
          />
        </Grid>

        <Grid item xs={12}>
          <Typography variant="subtitle1">Giorni Abilitati</Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid
            container
            direction={"row"}
            mb={2}
            display={"flex"}
            justifyContent={"space-between"}
            border={"2px solid #ccc"}
            borderRadius={2}
            padding={0}
            margin={0}
          >
            <Controller
              name={"recurringBooking.giorniAbilitati"}
              control={props.methods.control}
              render={({ field }) => {
                const giorni: {
                  label: string;
                  key: keyof RecurringBookingFormMain["giorniAbilitati"];
                }[] = [
                  { label: "Lunedì", key: "lunedi" },
                  { label: "Martedì", key: "martedi" },
                  { label: "Mercoledì", key: "mercoledi" },
                  { label: "Giovedì", key: "giovedi" },
                  { label: "Venerdì", key: "venerdi" },
                  { label: "Sabato", key: "sabato" },
                  { label: "Domenica", key: "domenica" },
                ];

                return (
                  <>
                    {giorni.map(({ label, key }) => (
                      <Grid item xs={6} sm={3} md={"auto"} key={key} ml={1}>
                        <FormControl>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={field.value?.[key] ?? false}
                                onChange={(e) => {
                                  field.onChange({
                                    ...field.value,
                                    [key]: e.target.checked,
                                  });
                                }}
                              />
                            }
                            label={label}
                          />
                        </FormControl>
                      </Grid>
                    ))}
                  </>
                );
              }}
            />
          </Grid>
        </Grid>

        <Grid item xs={12}>
          {/* Impostazioni Extra */}
          <Typography variant="subtitle1" gutterBottom>
            Impostazioni Extra
          </Typography>
          <Grid container spacing={2}>
            {/* Giorni Adiacenti + Tolleranza in minuti (vicini) */}
            <Grid item xs={6} sm={4} md={2}>
              <Controller
                name={"recurringBooking.giorniAdiacenti"}
                control={props.methods.control}
                render={({ field }) => {
                  return (
                    <FormControl>
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={field.value || false}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="Giorni Adiacenti"
                      />
                    </FormControl>
                  );
                }}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Controller
                name={"recurringBooking.orariAdiacenti"}
                control={props.methods.control}
                render={({ field }) => {
                  return (
                    <FormControl>
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={field.value || false}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="Orari Adiacenti"
                      />
                    </FormControl>
                  );
                }}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2} />
            <Grid item xs={6} sm={4} md={6}>
              <Controller
                name="recurringBooking.tolleranzaMinuti"
                control={props.methods.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    type="number"
                    value={field.value ?? ""}
                    inputProps={{ min: 0, max: 60 }}
                    InputLabelProps={{ shrink: true }}
                    label="Tolleranza in minuti"
                    placeholder="Inserire tolleranza minuti"
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 2);
                      field.onChange(value);
                    }}
                    onKeyDown={(e) => {
                      if (
                        [
                          "Backspace",
                          "Tab",
                          "ArrowLeft",
                          "ArrowRight",
                          "Delete",
                        ].includes(e.key)
                      ) {
                        return;
                      }

                      if (!/^[0-9]$/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                )}
              />
            </Grid>

            {/* Seconda riga con Orari Fissi e altre checkbox */}
            <Grid item xs={6} sm={4} md={2}>
              <Controller
                name={"recurringBooking.orariFissi"}
                control={props.methods.control}
                render={({ field }) => {
                  return (
                    <FormControl>
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={field.value || false}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="Orari Fissi"
                      />
                    </FormControl>
                  );
                }}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Controller
                name={"recurringBooking.sequenzaFissa"}
                control={props.methods.control}
                render={({ field }) => {
                  return (
                    <FormControl>
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={field.value || false}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="Sequenza Fissa"
                      />
                    </FormControl>
                  );
                }}
              />
            </Grid>
            <Grid item xs={6} sm={4} md={2}>
              <Controller
                name={"recurringBooking.risorsaFissa"}
                control={props.methods.control}
                render={({ field }) => {
                  return (
                    <FormControl>
                      <FormControlLabel
                        control={
                          <Checkbox
                            {...field}
                            checked={field.value || false}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="Risorsa Fissa"
                      />
                    </FormControl>
                  );
                }}
              />
            </Grid>

            {/* Campo finale Giorni Intervallo */}
            <Grid item xs={6} sm={4} md={6}>
              <Controller
                name="recurringBooking.giorniIntervalloPrestazioni"
                control={props.methods.control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    size="small"
                    fullWidth
                    type="number"
                    onChange={(e) => {
                      const value = e.target.value.slice(0, 3);
                      field.onChange(value);
                    }}
                    value={field.value ?? ""}
                    inputProps={{ min: 0 }}
                    InputLabelProps={{ shrink: true }}
                    label="Giorni di Intervallo tra le prestazioni"
                    placeholder="Inserire n° Giorni"
                    onKeyDown={(e) => {
                      if (
                        [
                          "Backspace",
                          "Tab",
                          "ArrowLeft",
                          "ArrowRight",
                          "Delete",
                        ].includes(e.key)
                      ) {
                        return;
                      }

                      if (!/^[0-9]$/.test(e.key)) {
                        e.preventDefault();
                      }
                    }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Grid>

        {/* Pulsanti */}
        {/* <Grid item xs={12} display={"flex"} justifyContent={"end"} gap={2}>
          <Button variant="outlined" color="primary">
            Lista Attesa
          </Button>
        </Grid> */}
      </Grid>
    </Box>
  );
};

export default RecurringBookingMainForm;
